import { Component, Input, OnChanges, OnInit, SimpleChanges } from "@angular/core";
import {
  EvaluationFlowsEnum,
  OverviewInfo,
  OverviewMapValue,
  OverviewSectionsDTO,
  OverviewSectionsEnum
} from "../../models/shared.model";
import { RiskAssessmentService } from "../../services/risk-assessment.service";
import { MatDialog } from "@angular/material/dialog";
import { DomainDialog } from "../domain-dialog/domain-dialog.component";
import { RiskLevel } from "../../models/risk-level";
import { ScrollToSectionService } from "../../services/scrollToSection.service";
import { WaitService } from "../../services/wait.service";
import { SharedService } from "../../services/shared.service";
import { TranslateService } from "@ngx-translate/core";
import { LogService } from "../../services/log.service";
import { ToastrService } from "ngx-toastr";
import { MailUtils } from "../../utils/mail.utils";
import { HttpClient } from "@angular/common/http";
import { environment } from "../../../environments/environment";
import { SettingsService } from "../../services/settings.service";
import { SettingsEnum } from "../../models/settings.model";
import {
  ConfirmationDialogComponent,
  ConfirmationDialogData
} from "../settings/confirmation-dialog/confirmation-dialog.component";
import { firstValueFrom } from "rxjs";

@Component({
  selector: "app-overview",
  templateUrl: "./overview.component.html",
  styleUrls: ["./overview.component.css"]
})
export class OverviewComponent implements OnInit, OnChanges {

  @Input() verdictAvailable = false;
  @Input("needleValue") needleValue: number;
  @Input("overviewMap") overviewMap: Map<string, OverviewMapValue>;
  @Input("evaluationScore") evaluationScore: Map<string, number> = new Map();

  private overviewSectionsDto: OverviewSectionsDTO[];
  protected readonly EvaluationFlowsEnum = EvaluationFlowsEnum;

  isAnalyzing: boolean = true;

  riskLevelLabel: string;
  barPosition = "0";
  graphBaseUrl = environment.apiConfig.microsoft_graph_url_personal;

  adminEmail = '';
  allowDeleteEmail= false;

  constructor(
    protected riskService: RiskAssessmentService,
    public dialog: MatDialog,
    private scrollToSectionService: ScrollToSectionService,
    private waitService: WaitService,
    private sharedService: SharedService,
    private translate: TranslateService,
    private logService: LogService,
    private toastrService: ToastrService,
    private httpClient: HttpClient,
    private settingsService: SettingsService,
  ) {
    this.overviewSectionsDto = this.riskService.getOverviewSections();
  }

  async ngOnInit(): Promise<void> {
    this.isAnalyzing = this.waitService.isAnalyzing;

    this.waitService.isAnalyzingObserver.subscribe((isAnalyzing) => {
      this.isAnalyzing = isAnalyzing;
    });

    await this.loadReportAsPhishingSettings();
    this.setNeedleValueAndLabel();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["needleValue"] && changes["needleValue"].currentValue != null) {
      this.needleValue = changes["needleValue"].currentValue;
      this.setNeedleValueAndLabel();
    }
  }

  setNeedleValueAndLabel(){
    if (this.needleValue == 100)
      this.barPosition = (0).toString() + "%";

    else
      this.barPosition = (100 - this.needleValue).toString() + "%";

    this.riskLevelLabel = this.needleValue <= 33 ? RiskLevel.Low :
      (this.needleValue <= 66 ? RiskLevel.Moderate : RiskLevel.High);
  }

  getSectionDTO(section: string){
    return this.overviewSectionsDto.find(value => value.name == section)
  }

  getSectionLimit(section: string) {
    const sectionDTO = this.getSectionDTO(section);

    return sectionDTO?.item_limit ?? 6;
  }

  getRiskLevelColor() {
    if (this.riskLevelLabel == RiskLevel.Low)
      return "text-green-2-color me-2";
    if (this.riskLevelLabel == RiskLevel.Moderate)
      return "text-orange-color";
    if (this.riskLevelLabel == RiskLevel.High)
      return "warning";

    return "";
  }

  showScoreInfo() {
    const dialogRef = this.dialog.open(DomainDialog, {
      width: "90%",
      height: "50%",
      data: this.evaluationScore
    });
  }

  asIsOrder(a, b) {
    return 1;
  }

  goToItem(flow: string, component: string, overviewSection: string, decision: string) {
    if(!component)
      return;
    this.scrollToSectionService.scrollToSection({
      id: component,
      isSectionReasonForConcern: overviewSection === OverviewSectionsEnum.REASONS_CONC,
      tab: this.scrollToSectionService.fromEvaluationFlowToTab(flow),
      decision: decision,
      flowName: flow,
    })
  }

  getOverviewIcon(overviewText: OverviewInfo) {
    if(overviewText.isMultipleCondition)
      return "ms-Icon ms-Icon--GroupList";

    let icon = "ms-Icon ms-Icon--";
    switch (overviewText.flow) {
      case EvaluationFlowsEnum.IMPERSONATION_RISK:
      case EvaluationFlowsEnum.SENDER:
        return icon += "Mail";
      case EvaluationFlowsEnum.LINKS:
        return icon += "Link linkIcon";
      case EvaluationFlowsEnum.ATTACHMENTS:
        return icon += "Attach";
      case EvaluationFlowsEnum.QRCODE:
        return icon += "QRCode";
      default:
        return icon += "GroupList";
    }
  }

  async loadReportAsPhishingSettings(){
    const adminEmailSetting = this.settingsService.getSettingValue(SettingsEnum.ADMINISTRATOR_EMAIL_TO_REPORT_TO);
    const sharedFolderEmail = await this.sharedService.getOwnerOfSharedFolder();

    if(adminEmailSetting && adminEmailSetting != sharedFolderEmail)
      this.adminEmail = adminEmailSetting

    this.allowDeleteEmail = this.settingsService.getSettingValue(SettingsEnum.ENABLE_DELETE_PHISHING_EMAILS) == true;
  }

  async onReportPhishing(): Promise<void> {

    const item = Office.context.mailbox.item;

    if (!item) {
      console.error("No email item found in context.");
      return;
    }

    const itemId = MailUtils.getItemRestId();

    try {
      const messageJson = await this.getEmailById(itemId);

      const { data, fileName } = this.logService.generateLogFileAndName();

      const base64 = btoa(
        String.fromCharCode(...new TextEncoder().encode(data))
      );

      const sent = await this.forwardEmailToAdmin(messageJson, this.adminEmail, base64, fileName);
      if (sent) {
        if(this.allowDeleteEmail)
          await this.moveEmailToDeleted(itemId);

        this.toastrService.success(this.translate.instant("overview.phishing_reported_success"));
      } else {
        this.toastrService.error(this.translate.instant("overview.failed_to_send_phishing_report"));
      }

    } catch (err) {
      console.error("Unexpected error during report phishing:", err);
    }
  }

  private getEmailById(itemId: string) {
    const url = `${this.graphBaseUrl}/messages/${itemId}`;
    return firstValueFrom(this.httpClient.get(url));
  }

  private async forwardEmailToAdmin(
      messageJson: any,
      adminEmail: string,
      base64: string,
      fileName: string
  ): Promise<boolean> {
    const url = `${this.graphBaseUrl}/sendMail`;
    const subject = messageJson.subject || "[No Subject]";
    try {
      await this.httpClient.post(url, {
        message: {
          subject: `[Reported as phishing] ${subject}`,
          body: {
            contentType: "HTML",
            content: `
            This email was reported as phishing by the user.
            Original message body:
            ${messageJson.body?.content}
          `
          },
          toRecipients: [
            { emailAddress: { address: adminEmail } }
          ],
          attachments: [
            {
              "@odata.type": "#microsoft.graph.fileAttachment",
              name: fileName,
              contentType: "text/plain",
              contentBytes: base64
            }
          ]
        },
        saveToSentItems: true
      }).toPromise();
      return true;
    } catch {
      return false;
    }
  }

  private moveEmailToDeleted(itemId: string) {
    const url = `${this.graphBaseUrl}/messages/${itemId}/move`;
    return firstValueFrom(this.httpClient.post(url, { destinationId: "deleteditems" }));
  }

  async handleReportPhishingWithConfirm(): Promise<void> {
    const dialogData: ConfirmationDialogData = {
      message: this.allowDeleteEmail ? this.translate.instant("overview.report_as_phishing_confirm_delete_and_forward")
        : this.translate.instant("overview.report_as_phishing_confirm_forward"),
      confirmBtnText: this.translate.instant("shared.confirm"),
      cancelBtnText: this.translate.instant("shared.cancel")
    };

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: dialogData
    });

    const confirmed = await firstValueFrom(dialogRef.afterClosed());
    if (!confirmed) {
      return;
    }
    await this.onReportPhishing();
  }

  getReportAsPhishingTooltip() {
    if(this.allowDeleteEmail)
      return this.translate.instant("overview.report_as_phishing_with_delete");

    return this.translate.instant("overview.report_as_phishing_without_delete");
  }
}
